/**
 * Conversation API Service
 * Handles all conversation-related API operations
 */

import axios from 'axios';
import type { AxiosInstance } from 'axios';
type Conversation = any;
type Reply = any;
type UpdateConversationRequest = any;
type PaginationParams = any;
type ReplyFilter = any;
type EditHistoryItem = any;

class ConversationApiService
{
  private createAxiosInstance(apiUrl: string, token: string): AxiosInstance
  {
    return axios.create({
      baseURL: apiUrl,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
  }

  /**
   * Get conversation by ID
   */
  async getConversationById(
    apiUrl: string,
    token: string,
    conversationId: number,
    options?: {
      layersToHide?: string;
      getAllReplies?: boolean;
      isConversationDetailsView?: boolean;
      totalReplies?: number;
      isEnabled?: boolean;
    },
  ): Promise<Conversation>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.post(`/api/Conversation/GetConversationById`, null, {
      params: {
        conversationId,
        layersToHide: options?.layersToHide || '',
        getAllReplies: options?.getAllReplies ?? false,
        isConversationDetailsView: options?.isConversationDetailsView ?? true,
        totalReplies: options?.totalReplies ?? 0,
        isEnabled: options?.isEnabled ?? true,
      },
    });
    return response.data;
  }

  /**
   * Mark conversation as viewed/read
   */
  async setConversationAsViewed(
    apiUrl: string,
    token: string,
    conversationId: number,
  ): Promise<void>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    await api.put(`/api/notification/SetViewedByConversationId`, null, {
      params: {
        conversationId,
      },
    });
  }

  /**
   * Update conversation title
   */
  async updateTitle(
    apiUrl: string,
    token: string,
    request: UpdateConversationRequest,
  ): Promise<Conversation>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.put('/api/conversations/update-title', {
      conversations: [request],
    });
    return response.data;
  }

  /**
   * Update conversation body
   */
  async updateBody(
    apiUrl: string,
    token: string,
    request: UpdateConversationRequest,
  ): Promise<Conversation>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.put('/api/conversations/update-body', {
      conversations: [request],
    });
    return response.data;
  }

  /**
   * Update conversation complete/finished status
   */
  async updateComplete(
    apiUrl: string,
    token: string,
    params: {
      ConversationId: number;
      Finished: boolean;
      DashboardGroupId?: number;
      SocketConnectionId?: string;
    },
  ): Promise<boolean>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.post('/api/conversations/update-complete', params);
    return response.data;
  }

  /**
   * Update red flag status
   */
  async updateRedFlags(
    apiUrl: string,
    token: string,
    request: { conversations: UpdateConversationRequest[] },
  ): Promise<void>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    await api.put('/api/conversations/update-redflags', request);
  }

  /**
   * Update important status
   */
  async updateImportant(
    apiUrl: string,
    token: string,
    request: { conversations: UpdateConversationRequest[] },
  ): Promise<void>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    await api.put('/api/conversations/update-important', request);
  }

  /**
   * Follow/unfollow conversation
   */
  async toggleFollow(
    apiUrl: string,
    token: string,
    conversationId: number,
    follow: boolean,
  ): Promise<void>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    await api.post(`/api/conversations/${conversationId}/follow`, { follow });
  }

  /**
   * Archive/unarchive conversation
   */
  async toggleArchive(
    apiUrl: string,
    token: string,
    conversationId: number,
    archive: boolean,
  ): Promise<void>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    await api.post(`/api/conversations/${conversationId}/archive`, { archive });
  }

  /**
   * Delete conversation
   */
  async deleteConversation(apiUrl: string, token: string, conversationId: number): Promise<void>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    await api.delete(`/api/conversations/${conversationId}`);
  }

  /**
   * Get more replies with pagination
   */
  async getMoreReplies(
    apiUrl: string,
    token: string,
    conversationId: number,
    pagination: PaginationParams,
    filter?: ReplyFilter,
  ): Promise<{ replies: Reply[]; hasMore: boolean; total: number }>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.post(`/api/conversations/${conversationId}/replies`, {
      pageNumber: pagination.pageNumber,
      pageSize: pagination.pageSize,
      phaseId: filter?.phaseId,
      replyTab: filter?.replyTab,
      searchText: filter?.searchText,
    });
    return response.data;
  }

  /**
   * Get reply IDs for a conversation with filter
   */
  async getReplyIdsFromConversationWithFilter(
    apiUrl: string,
    token: string,
    conversationId: number,
    filter?: ReplyFilter,
  ): Promise<number[]>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.post(`/api/conversations/${conversationId}/reply-ids`, filter);
    return response.data;
  }

  /**
   * Get tag list (people and groups for mentions)
   */
  async getTagList(
    apiUrl: string,
    token: string,
    projectId: number,
  ): Promise<{ people: any[]; groups: any[] }>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.get(`/api/projects/${projectId}/tags`);
    return response.data;
  }

  /**
   * Copy conversation to another project
   */
  async copyToProject(
    apiUrl: string,
    token: string,
    conversationId: number,
    targetProjectId: number,
  ): Promise<Conversation>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.post(`/api/conversations/${conversationId}/copy`, {
      targetProjectId,
    });
    return response.data;
  }

  /**
   * Move conversation to another project
   */
  async moveToProject(
    apiUrl: string,
    token: string,
    conversationId: number,
    targetProjectId: number,
  ): Promise<Conversation>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.post(`/api/conversations/${conversationId}/move`, {
      targetProjectId,
    });
    return response.data;
  }

  /**
   * Get edit history for conversation
   */
  async getEditHistory(
    apiUrl: string,
    token: string,
    conversationId: number,
  ): Promise<EditHistoryItem[]>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.get(`/api/conversations/${conversationId}/history`);
    return response.data;
  }

  /**
   * Update conversation layers
   */
  async updateLayers(
    apiUrl: string,
    token: string,
    conversationId: number,
    layerIds: number[],
  ): Promise<void>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    await api.put(`/api/conversations/${conversationId}/layers`, { layerIds });
  }

  /**
   * Update conversation owners
   */
  async updateOwners(
    apiUrl: string,
    token: string,
    conversationId: number,
    ownerIds: number[],
  ): Promise<void>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    await api.put(`/api/conversations/${conversationId}/owners`, { ownerIds });
  }

  /**
   * Update conversation security groups
   */
  async updateSecurityGroups(
    apiUrl: string,
    token: string,
    conversationId: number,
    securityGroupIds: number[],
  ): Promise<void>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    await api.put(`/api/conversations/${conversationId}/security-groups`, {
      securityGroupIds,
    });
  }

  /**
   * Create new conversation phase
   */
  async createPhase(
    apiUrl: string,
    token: string,
    conversationId: number,
    title: string,
    description?: string,
  ): Promise<any>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.post(`/api/conversations/${conversationId}/phases`, {
      title,
      description,
    });
    return response.data;
  }

  /**
   * Update conversation phase
   */
  async updatePhase(
    apiUrl: string,
    token: string,
    conversationId: number,
    phaseId: number,
    title: string,
    description?: string,
  ): Promise<void>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    await api.put(`/api/conversations/${conversationId}/phases/${phaseId}`, {
      title,
      description,
    });
  }

  /**
   * Delete conversation phase
   */
  async deletePhase(
    apiUrl: string,
    token: string,
    conversationId: number,
    phaseId: number,
  ): Promise<void>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    await api.delete(`/api/conversations/${conversationId}/phases/${phaseId}`);
  }

  /**
   * Get All phase
   */
  async GetAllPhases(apiUrl: string, token: string): Promise<any>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.get(`/api/Conversation/getallphases`);
    return response.data;
  }

  /**
   * Get CustomFields By Item Id
   */
  async GetCustomFieldsByItemId(
    apiUrl: string,
    token: string,
    CoreTaskId: number,
    IsDeleted: boolean,
    ProjectId: number,
  ): Promise<any>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.get(
      `/api/CustomField/GetCustomFieldsByItemId?CoreTaskId=${CoreTaskId}&IsDeleted=${IsDeleted}&ProjectId=${ProjectId}`,
    );
    return response.data;
  }

  /**
   * Get Empty CustomFields By ConfigValue Id
   */
  async GetEmptyCustomFieldsByConfigValueId(
    apiUrl: string,
    token: string,
    configValueId: number,
  ): Promise<any>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.get(
      `/api/configvalue/GetEmptyCustomFieldsByConfigValueId?configValueId=${configValueId}`,
    );
    return response.data;
  }

  /**
   * Create New Option Phase
   */
  async CreateNewOptionPhase(apiUrl: string, token: string, item: any): Promise<any>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.post(`/api/Phase/CreateNewOptionPhase`, item);
    return response.data;
  }

  /**
   * Conversation New Phase
   */
  async ConversationNewPhase(apiUrl: string, token: string, item: any): Promise<any>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.post(`/api/Conversation/newphase`, item);
    return response.data;
  }

  /**
   * Create New Phase
   */
  async CreateNewPhase(
    //newcard
    apiUrl: string,
    token: string,
    item: any,
  ): Promise<any>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.post(`/api/Conversation/createphase`, item);
    return response.data;
  }

  /**
   * Create New Phase
   */
  async GetValidationPhase(
    //newcard
    apiUrl: string,
    token: string,
    Conversations: any,
    Phases: any,
  ): Promise<any>
  {
    const urlParams = '?conversations=' + Conversations + '&phases=' + Phases;
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.get(`/api/Conversation/validationforphases${urlParams}`);
    return response.data;
  }

  /**
   * Update multiple conversations
   */
  async Updatemultipleconversations(
    //newcard
    apiUrl: string,
    token: string,
    requestParams: any,
  ): Promise<any>
  {
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.post(`/api/conversation/updatemultipleconversations`, requestParams);
    return response.data;
  }

  /**
   * Get Conversations Hierarchy
   */
  async GetConversationsHierarchy(
    //newcard
    apiUrl: string,
    token: string,
    requestParams: any,
  ): Promise<any>
  {
    const { conversationIds, dashboardId } = requestParams;
    let urlParams = `?ConversationIds=${conversationIds}`;
    urlParams += `&DashboardId=${dashboardId}`;
    const api = this.createAxiosInstance(apiUrl, token);
    const response = await api.get(
      `/api/Conversation/GetConversationsHierarchy${urlParams}`,
      requestParams,
    );
    return response.data;
  }
}

export const conversationApi = new ConversationApiService();
export default conversationApi;
